import { useEffect, useState } from 'react';
import { IconClockHour4 } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { offeredTaskKeys, rewardSum } from '../../utils/contestTasks';
import { CARD, useNumFmt } from './contestUi';

// "My standing": rank + points, what is needed for the next goal, and where the points come from.
// `me` is /api/contest/me (rank is null when outside the board); the board rows come from the public
// leaderboard, so the gaps are worked out from the same numbers everybody sees.

const pctOf = (v, max) => (max > 0 ? Math.min(100, Math.max(3, (v / max) * 100)) : 0);
const round2 = (n) => Math.max(1, Math.round(n * 100) / 100);

export default function ContestStandingCard({ me, phase, leaderboard = [], winnerCount = 50 }) {
    const { t } = useTranslation();
    const fmt = useNumFmt();

    const live = phase === 'live';
    const inBoard = me.rank != null;
    const showRank = inBoard && (live || phase === 'completed');
    const points = me.points || {};
    const myPts = points.total ?? 0;

    // Lets the progress bar grow from 0 on first paint.
    const [ready, setReady] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setReady(true));
        return () => cancelAnimationFrame(id);
    }, []);

    // ── status chip ──────────────────────────────────────────────────────────
    let chip = null;
    if (live || phase === 'completed') {
        if (inBoard && me.rank === 1) chip = { key: 'chipLeader', tone: 'good' };
        else if (inBoard && me.rank <= winnerCount) chip = { key: 'chipInZone', tone: 'good' };
        else chip = { key: 'chipOutside', tone: 'warn' };
    }

    // ── goal (live) / one honest line per phase (everything else) ───────────
    const byRank = new Map(leaderboard.map((e) => [e.rank, e]));
    // Bonus points still to be picked up: a task that is on offer right now (the same list the bonus-task card shows — one app review
    // at a time, no install already paid in an earlier contest) and has no claim yet; share counts for today only.
    const openBonus = rewardSum(me, offeredTaskKeys(me).filter((key) => !(me.claims || []).some((c) => c.activity === key && (key !== 'share' || c.claimDate === me.today))));

    let goal = { title: '', sub: '', pct: 0 };
    if (live) {
        const cut = leaderboard[winnerCount - 1]; // the last winning place
        if (inBoard && me.rank === 1) {
            const second = byRank.get(2);
            goal = {
                title: second ? t('contest.page.gapLead', { n: fmt(Math.max(0, Math.round((myPts - second.point) * 100) / 100)) }) : t('contest.page.gapLeadAlone'),
                sub: t('contest.page.tipLead'),
                pct: second && myPts > 0 ? Math.round((second.point / myPts) * 100) : 100,
            };
        } else if (inBoard && me.rank <= winnerCount) {
            const above = byRank.get(me.rank - 1);
            goal = {
                title: above ? t('contest.page.gapNext', { n: fmt(round2(above.point - myPts + 1)), rank: fmt(me.rank - 1) }) : t('contest.leaderboard.yourPosition'),
                sub: openBonus > 0 ? t('contest.page.tipBonus', { n: fmt(openBonus) }) : t('contest.page.tipInvite'),
                pct: above ? pctOf(myPts, above.point) : 0,
            };
        } else if (cut && leaderboard.length >= winnerCount) {
            goal = {
                title: t('contest.page.gapWinners', { n: fmt(round2(cut.point - myPts + 1)) }),
                sub: t('contest.page.tipCut', { rank: fmt(winnerCount), pts: fmt(cut.point) }),
                pct: pctOf(myPts, cut.point),
            };
        } else {
            goal = { title: t('contest.page.gapJoin'), sub: t('contest.page.tipInvite'), pct: 0 };
        }
    } else {
        goal.title = phase === 'completed' ? t(inBoard ? 'contest.page.finalPosition' : 'contest.page.notInFinal')
            : phase === 'pending' ? t('contest.page.resultsPending')
            : t('contest.page.notStarted');
    }

    // ── where the points come from ───────────────────────────────────────────
    const parts = [
        { key: 'fromUsage', v: points.credit ?? 0, color: 'bg-primary' },
        { key: 'fromOwnPayment', v: points.ownPayment ?? 0, color: 'bg-primary/50' },
        { key: 'fromReferral', v: points.referral ?? 0, color: 'bg-doc-blue-600' },
        { key: 'fromBonus', v: points.claims ?? 0, color: 'bg-emerald-500' },
    ];
    const total = parts.reduce((n, p) => n + p.v, 0);

    const chipCls = chip?.tone === 'good'
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
        : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';

    return (
        <section className={`${CARD} grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] items-center gap-x-7 gap-y-5 px-5 py-5 sm:px-6 sm:py-[22px]`}>
            {/* Rank + points */}
            <div className="flex items-center gap-4">
                <div className="flex h-[68px] w-[68px] flex-none flex-col items-center justify-center rounded-[18px] border border-primary/20 bg-primary/10">
                    <div className="text-xs font-semibold leading-none text-primary">{t('contest.leaderboard.rankShort')}</div>
                    <div className="font-baloo text-[30px] font-extrabold leading-[1.1] text-primary">
                        {showRank ? fmt(me.rank) : (phase === 'pending' ? <IconClockHour4 size={26} /> : '—')}
                    </div>
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                    <div className="text-sm text-zinc-500 dark:text-zinc-400">{t('contest.earn.myPoints')}</div>
                    {/* The points, with the status chip on their right */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <div className="font-baloo text-[30px] font-bold leading-[1.1] text-zinc-900 dark:text-zinc-100">{fmt(myPts)}</div>
                        {chip && <span className={`rounded-full px-2.5 py-px text-[13px] font-semibold ${chipCls}`}>{t(`contest.page.${chip.key}`)}</span>}
                    </div>
                </div>
            </div>

            {/* Goal */}
            <div className="flex flex-col gap-2">
                <div className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{goal.title}</div>
                {live && (
                    <div className="h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                        <div className="h-full rounded-full bg-primary transition-[width] duration-1000 ease-out" style={{ width: ready ? `${Math.min(100, goal.pct)}%` : '0%' }} />
                    </div>
                )}
                {goal.sub && <div className="text-[13px] text-zinc-500 dark:text-zinc-400">{goal.sub}</div>}
            </div>

            {/* Breakdown */}
            <div className="flex flex-col gap-2.5">
                <div className="flex h-2 gap-0.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                    {total > 0 && parts.filter((p) => p.v > 0).map((p) => (
                        <div key={p.key} className={p.color} style={{ width: `${((p.v / total) * 100).toFixed(1)}%` }} />
                    ))}
                </div>
                <div className="grid grid-cols-2 gap-x-3.5 gap-y-1">
                    {parts.map((p) => (
                        <div key={p.key} className="flex min-w-0 items-center gap-[7px] text-[13px] text-zinc-500 dark:text-zinc-400">
                            <span className={`h-2 w-2 flex-none rounded-[2px] ${p.color}`} />
                            <span className="min-w-0 flex-1 truncate">{t(`contest.earn.${p.key}`)}</span>
                            <span className="font-semibold text-zinc-900 dark:text-zinc-100">{fmt(p.v)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
